<script setup>
import { computed, onMounted, ref, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  campaignId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const campaignId = toRef(props, 'campaignId');

const summary = useFunctionGetter('campaigns/getOperationsSummary', campaignId);
const uiFlags = useFunctionGetter(
  'campaigns/getOperationsSummaryUIFlags',
  campaignId
);
const approvalWorkflowUIFlags = useFunctionGetter(
  'campaigns/getApprovalWorkflowUIFlags',
  campaignId
);
const canaryApprovalUIFlags = useFunctionGetter(
  'campaigns/getCanaryApprovalUIFlags',
  campaignId
);
const deliveryReconciliationUIFlags = useFunctionGetter(
  'campaigns/getDeliveryReconciliationUIFlags',
  campaignId
);

const EMPTY_VALUE = '-';
const IN_REVIEW_STATUS = 'in_review';
const APPROVED_STATUS = 'approved';
const CANARY_LIMIT_REASON = 'canary_limit_reached';
const MANUAL_FAILED_RECONCILIATION_ERROR_CODE = 'manual_reconciliation_failed';
const READY_OPERATIONAL_STATES = ['finished', 'idle', 'ready_to_resume'];
const DEGRADED_OPERATIONAL_STATES = [
  'backpressure_paused',
  'running_healthy',
  'stale_run_recovery',
];
const BLOCKED_OPERATIONAL_STATES = [
  'awaiting_approval',
  'paused_manual',
  'provider_readiness_blocked',
  'waiting_canary_approval',
];

const approvalReason = ref('');
const canaryReason = ref('');
const reconciliationReason = ref('');
const activeApprovalAction = ref(null);
const activeReconciliationAction = ref(null);
const pendingRequestChangesStageKey = ref(null);

const hasValue = value => value !== undefined && value !== null && value !== '';
const displayValue = value => (hasValue(value) ? String(value) : EMPTY_VALUE);
const displayCount = value => (hasValue(value) ? String(value) : '0');
const arrayValue = value => (Array.isArray(value) ? value : []);
const objectValue = value => (value && typeof value === 'object' ? value : {});
const numberValue = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};
const uniqueValues = values => [
  ...new Set(arrayValue(values).filter(hasValue).map(String)),
];
const firstPresentValue = values => values.find(hasValue);

const formatDate = value => {
  if (!value) return EMPTY_VALUE;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const toneForStatus = value => {
  const normalizedValue = String(value || '').toLowerCase();

  if (
    [
      'approved',
      'completed',
      'delivered',
      'ready',
      'succeeded',
      'success',
    ].includes(normalizedValue)
  ) {
    return 'bg-n-teal-3 text-n-teal-11';
  }

  if (
    [
      'failed',
      'error',
      'cancelled',
      'canceled',
      'rejected',
      'unknown',
      'stale_run_recovery',
      'needs_unknown_reconciliation',
      'provider_readiness_blocked',
      'blocked',
      'unavailable',
    ].includes(normalizedValue)
  ) {
    return 'bg-n-ruby-3 text-n-ruby-11';
  }

  if (
    [
      'paused',
      'in_review',
      'pending',
      'scheduled',
      'running',
      'waiting_canary_approval',
      'backpressure_paused',
      'awaiting_approval',
      'degraded',
      'paused_manual',
    ].includes(normalizedValue)
  ) {
    return 'bg-n-amber-3 text-n-amber-11';
  }

  if (
    ['idle', 'ready_to_resume', 'reconciling', 'running_healthy'].includes(
      normalizedValue
    )
  ) {
    return 'bg-n-blue-3 text-n-blue-11';
  }

  return 'bg-n-alpha-2 text-n-slate-11';
};

const deliverySummary = computed(() => summary.value?.deliveries || {});
const trackedLinksSummary = computed(() =>
  objectValue(deliverySummary.value.tracked_links)
);
const latestTrackedLinkClicks = computed(() =>
  arrayValue(trackedLinksSummary.value.latest_clicks)
);
const receiptSummary = computed(() => summary.value?.receipts || {});
const latestRun = computed(() => summary.value?.latest_run || {});
const campaignSummary = computed(() => summary.value?.campaign || {});
const canary = computed(() => summary.value?.canary || {});
const approval = computed(() => summary.value?.approval || {});
const promotionReadiness = computed(
  () => summary.value?.promotion_readiness || {}
);
const recovery = computed(() => summary.value?.recovery || {});
const providerReadiness = computed(
  () => summary.value?.provider_readiness || {}
);
const permissions = computed(() => summary.value?.permissions || {});
const latestRunMetrics = computed(() => objectValue(latestRun.value.metrics));
const backpressure = computed(() => objectValue(latestRun.value.backpressure));
const deliveryReconciliation = computed(() =>
  objectValue(latestRun.value.delivery_reconciliation)
);
const cursor = computed(() => latestRun.value.cursor || {});
const batchJob = computed(() => latestRun.value.batch_job || {});

const providerReadinessChecks = computed(() =>
  arrayValue(providerReadiness.value.checks)
);

const providerReadinessLatestBlock = computed(
  () => providerReadiness.value.latest_block || {}
);

const hasProviderReadiness = computed(
  () => Object.keys(providerReadiness.value).length > 0
);

const providerReadinessState = computed(() => {
  if (!hasProviderReadiness.value) return 'unknown';
  if (providerReadiness.value.ready === true) return 'ready';
  if (providerReadiness.value.required === true) return 'blocked';
  return 'not_required';
});

const providerReadinessStatusLabel = computed(() => {
  if (providerReadinessState.value === 'ready') {
    return t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.STATUS.READY');
  }

  if (providerReadinessState.value === 'blocked') {
    return t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.STATUS.BLOCKED');
  }

  if (providerReadinessState.value === 'not_required') {
    return t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.STATUS.NOT_REQUIRED');
  }

  return t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.STATUS.UNKNOWN');
});

const providerReadinessTone = computed(
  () =>
    ({
      ready: 'bg-n-teal-3 text-n-teal-11',
      blocked: 'bg-n-ruby-3 text-n-ruby-11',
      not_required: 'bg-n-alpha-2 text-n-slate-11',
    })[providerReadinessState.value] || 'bg-n-alpha-2 text-n-slate-11'
);

const providerReadinessWarnings = computed(() =>
  arrayValue(providerReadiness.value.warnings)
);

const recoveryState = computed(() =>
  String(recovery.value.state || '').toLowerCase()
);

const hasPromotionReadiness = computed(
  () => Object.keys(promotionReadiness.value).length > 0
);

const hasBackpressureSignal = computed(
  () =>
    Object.keys(backpressure.value).length > 0 ||
    recoveryState.value === 'backpressure_paused'
);

const hasDeliveryReconciliationMetadata = computed(
  () => Object.keys(deliveryReconciliation.value).length > 0
);

const isProviderReadinessBlocking = computed(
  () =>
    providerReadinessState.value === 'blocked' ||
    recoveryState.value === 'provider_readiness_blocked'
);

const isDeliveryReconciliationActive = computed(
  () =>
    recoveryState.value === 'needs_unknown_reconciliation' ||
    numberValue(deliverySummary.value.unknown_count) > 0 ||
    String(deliveryReconciliation.value.state || '').toUpperCase() === 'UNKNOWN'
);

const operationalState = computed(() => {
  if (isProviderReadinessBlocking.value) return 'blocked';
  if (isDeliveryReconciliationActive.value) return 'reconciling';
  if (BLOCKED_OPERATIONAL_STATES.includes(recoveryState.value)) {
    return 'blocked';
  }
  if (
    DEGRADED_OPERATIONAL_STATES.includes(recoveryState.value) ||
    hasBackpressureSignal.value ||
    providerReadinessWarnings.value.length > 0 ||
    providerReadinessState.value === 'unknown'
  ) {
    return 'degraded';
  }
  if (READY_OPERATIONAL_STATES.includes(recoveryState.value)) return 'ready';

  return 'ready';
});

const operationalStateLabel = computed(() => {
  switch (operationalState.value) {
    case 'blocked':
      return t('CAMPAIGN.OPERATIONS.GATE.STATUS.BLOCKED');
    case 'degraded':
      return t('CAMPAIGN.OPERATIONS.GATE.STATUS.DEGRADED');
    case 'reconciling':
      return t('CAMPAIGN.OPERATIONS.GATE.STATUS.RECONCILING');
    default:
      return t('CAMPAIGN.OPERATIONS.GATE.STATUS.READY');
  }
});

const operationalStateDescription = computed(() => {
  switch (operationalState.value) {
    case 'blocked':
      return t('CAMPAIGN.OPERATIONS.GATE.DESCRIPTION.BLOCKED');
    case 'degraded':
      return t('CAMPAIGN.OPERATIONS.GATE.DESCRIPTION.DEGRADED');
    case 'reconciling':
      return t('CAMPAIGN.OPERATIONS.GATE.DESCRIPTION.RECONCILING');
    default:
      return t('CAMPAIGN.OPERATIONS.GATE.DESCRIPTION.READY');
  }
});

const operationalSignalItems = computed(() => [
  {
    key: 'provider',
    label: t('CAMPAIGN.OPERATIONS.GATE.SIGNALS.PROVIDER'),
    value: providerReadinessStatusLabel.value,
    tone: providerReadinessTone.value,
  },
  {
    key: 'backpressure',
    label: t('CAMPAIGN.OPERATIONS.GATE.SIGNALS.BACKPRESSURE'),
    value: hasBackpressureSignal.value
      ? displayValue(backpressure.value.reason || recovery.value.reason)
      : t('CAMPAIGN.OPERATIONS.STATUS.NONE'),
    tone: hasBackpressureSignal.value
      ? toneForStatus('degraded')
      : toneForStatus('ready'),
  },
  {
    key: 'reconciliation',
    label: t('CAMPAIGN.OPERATIONS.GATE.SIGNALS.RECONCILIATION'),
    value: isDeliveryReconciliationActive.value
      ? t('CAMPAIGN.OPERATIONS.GATE.STATUS.RECONCILING')
      : t('CAMPAIGN.OPERATIONS.GATE.STATUS.READY'),
    tone: isDeliveryReconciliationActive.value
      ? toneForStatus('reconciling')
      : toneForStatus('ready'),
  },
  {
    key: 'promotion',
    label: t('CAMPAIGN.OPERATIONS.GATE.SIGNALS.PROMOTION'),
    value: displayValue(promotionReadiness.value.status),
    tone: toneForStatus(promotionReadiness.value.status),
  },
]);

const findProviderReadinessCheck = key =>
  providerReadinessChecks.value.find(check => check.key === key) || {};

const providerCredentialCheck = computed(() =>
  findProviderReadinessCheck('provider_credentials')
);

const whatsappTemplateCheck = computed(() =>
  findProviderReadinessCheck('whatsapp_template_approved')
);

const hasProviderCredentialCheck = computed(
  () => Object.keys(providerCredentialCheck.value).length > 0
);

const hasWhatsappTemplateCheck = computed(
  () => Object.keys(whatsappTemplateCheck.value).length > 0
);

const providerReadinessBlockers = computed(() =>
  uniqueValues([
    ...arrayValue(providerReadiness.value.blockers),
    ...arrayValue(providerReadinessLatestBlock.value.blockers),
  ])
);

const providerCredentialKeyGroups = computed(() => [
  {
    key: 'present',
    label: t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.CREDENTIALS.PRESENT'),
    values: uniqueValues(providerCredentialCheck.value.present_keys),
    tone: 'bg-n-teal-3 text-n-teal-11',
  },
  {
    key: 'missing',
    label: t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.CREDENTIALS.MISSING'),
    values: uniqueValues(providerCredentialCheck.value.missing_keys),
    tone: 'bg-n-ruby-3 text-n-ruby-11',
  },
]);

const hasProviderCredentialKeyDetails = computed(() =>
  providerCredentialKeyGroups.value.some(group => group.values.length > 0)
);

const providerReadinessDetails = computed(() => [
  {
    key: 'channel',
    label: t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.FIELDS.CHANNEL'),
    value: displayValue(providerReadiness.value.channel),
  },
  {
    key: 'provider',
    label: t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.FIELDS.PROVIDER'),
    value: displayValue(providerReadiness.value.provider),
  },
  {
    key: 'mode',
    label: t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.FIELDS.MODE'),
    value: displayValue(providerReadiness.value.mode),
  },
  {
    key: 'credential-source',
    label: t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.FIELDS.CREDENTIAL_SOURCE'),
    value: displayValue(providerReadiness.value.credential_source),
  },
  {
    key: 'target-account',
    label: t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.FIELDS.TARGET_ACCOUNT'),
    value: displayValue(providerReadiness.value.target_account_id),
  },
  {
    key: 'inbox',
    label: t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.FIELDS.INBOX'),
    value: displayValue(providerReadiness.value.inbox_id),
  },
]);

const providerReadinessLatestBlockDetails = computed(() => [
  {
    key: 'mode',
    label: t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.FIELDS.MODE'),
    value: displayValue(providerReadinessLatestBlock.value.mode),
  },
  {
    key: 'provider',
    label: t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.FIELDS.PROVIDER'),
    value: displayValue(providerReadinessLatestBlock.value.provider),
  },
  {
    key: 'channel',
    label: t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.FIELDS.CHANNEL'),
    value: displayValue(providerReadinessLatestBlock.value.channel),
  },
]);

const hasProviderReadinessLatestBlock = computed(
  () => Object.keys(providerReadinessLatestBlock.value).length > 0
);

const isProviderReadinessBlockedRecovery = computed(
  () => recovery.value.state === 'provider_readiness_blocked'
);

const providerReadinessCheckLabel = check => {
  switch (check.key) {
    case 'provider_readiness_mode':
      return t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.CHECKS.MODE');
    case 'target_account_scope':
      return t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.CHECKS.TARGET_ACCOUNT');
    case 'provider_readiness_authorized':
      return t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.CHECKS.AUTHORIZATION');
    case 'credential_source':
      return t(
        'CAMPAIGN.OPERATIONS.PROVIDER_READINESS.CHECKS.CREDENTIAL_SOURCE'
      );
    case 'provider_credentials':
      return t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.CHECKS.CREDENTIALS');
    case 'whatsapp_template_approved':
      return t(
        'CAMPAIGN.OPERATIONS.PROVIDER_READINESS.CHECKS.WHATSAPP_TEMPLATE'
      );
    default:
      return t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.CHECKS.UNKNOWN', {
        key: displayValue(check.key),
      });
  }
};

const providerReadinessBlockerLabel = blocker => {
  switch (blocker) {
    case 'target_account_missing':
      return t(
        'CAMPAIGN.OPERATIONS.PROVIDER_READINESS.BLOCKERS.TARGET_ACCOUNT_MISSING'
      );
    case 'target_account_mismatch':
      return t(
        'CAMPAIGN.OPERATIONS.PROVIDER_READINESS.BLOCKERS.TARGET_ACCOUNT_MISMATCH'
      );
    case 'provider_readiness_authorization_missing':
      return t(
        'CAMPAIGN.OPERATIONS.PROVIDER_READINESS.BLOCKERS.AUTHORIZATION_MISSING'
      );
    case 'credential_source_not_supported':
      return t(
        'CAMPAIGN.OPERATIONS.PROVIDER_READINESS.BLOCKERS.CREDENTIAL_SOURCE_NOT_SUPPORTED'
      );
    case 'provider_credentials_missing':
      return t(
        'CAMPAIGN.OPERATIONS.PROVIDER_READINESS.BLOCKERS.CREDENTIALS_MISSING'
      );
    case 'provider_sender_reference_missing':
      return t(
        'CAMPAIGN.OPERATIONS.PROVIDER_READINESS.BLOCKERS.SENDER_REFERENCE_MISSING'
      );
    case 'whatsapp_template_missing':
      return t(
        'CAMPAIGN.OPERATIONS.PROVIDER_READINESS.BLOCKERS.WHATSAPP_TEMPLATE_MISSING'
      );
    case 'whatsapp_template_not_approved':
      return t(
        'CAMPAIGN.OPERATIONS.PROVIDER_READINESS.BLOCKERS.WHATSAPP_TEMPLATE_NOT_APPROVED'
      );
    default:
      return t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.BLOCKERS.UNKNOWN', {
        reason: displayValue(blocker),
      });
  }
};

const booleanDisplay = value => {
  if (!hasValue(value)) return EMPTY_VALUE;

  return value
    ? t('CAMPAIGN.OPERATIONS.STATUS.YES')
    : t('CAMPAIGN.OPERATIONS.STATUS.NO');
};

const isInitialLoading = computed(
  () => uiFlags.value.isFetching && !summary.value
);

const statusItems = computed(() => {
  if (!summary.value) return [];

  const canaryEnabled = canary.value.enabled;
  let canaryLabel = EMPTY_VALUE;

  if (hasValue(canaryEnabled)) {
    canaryLabel = canaryEnabled
      ? t('CAMPAIGN.OPERATIONS.STATUS.ENABLED')
      : t('CAMPAIGN.OPERATIONS.STATUS.DISABLED');
  }

  return [
    {
      key: 'campaign',
      label: t('CAMPAIGN.OPERATIONS.STATUS.CAMPAIGN'),
      value: displayValue(summary.value.campaign?.status),
      tone: toneForStatus(summary.value.campaign?.status),
    },
    {
      key: 'approval',
      label: t('CAMPAIGN.OPERATIONS.STATUS.APPROVAL'),
      value: displayValue(summary.value.approval?.status),
      tone: toneForStatus(summary.value.approval?.status),
    },
    {
      key: 'canary',
      label: t('CAMPAIGN.OPERATIONS.STATUS.CANARY'),
      value: canaryLabel,
      tone: canaryEnabled
        ? 'bg-n-blue-3 text-n-blue-11'
        : 'bg-n-alpha-2 text-n-slate-11',
    },
    {
      key: 'run',
      label: t('CAMPAIGN.OPERATIONS.STATUS.RUN'),
      value: displayValue(latestRun.value.status),
      tone: toneForStatus(latestRun.value.status),
    },
    {
      key: 'recovery',
      label: t('CAMPAIGN.OPERATIONS.STATUS.RECOVERY'),
      value: displayValue(recovery.value.state),
      tone: toneForStatus(recovery.value.state),
      title: displayValue(recovery.value.reason),
    },
    {
      key: 'provider-readiness',
      label: t('CAMPAIGN.OPERATIONS.STATUS.PROVIDER_READINESS'),
      value: providerReadinessStatusLabel.value,
      tone: providerReadinessTone.value,
      title: displayValue(providerReadiness.value.mode),
    },
  ];
});

const metricItems = computed(() => [
  {
    key: 'audience',
    label: summary.value?.snapshot?.audience_count
      ? t('CAMPAIGN.OPERATIONS.METRICS.AUDIENCE_MATERIALIZED')
      : t('CAMPAIGN.OPERATIONS.METRICS.AUDIENCE_CONFIGURED'),
    value: displayCount(
      firstPresentValue([
        summary.value?.snapshot?.audience_count,
        campaignSummary.value.configured_audience_count,
      ])
    ),
    title: summary.value?.snapshot?.audience_count
      ? t('CAMPAIGN.OPERATIONS.METRICS.AUDIENCE_MATERIALIZED_HELP')
      : t('CAMPAIGN.OPERATIONS.METRICS.AUDIENCE_CONFIGURED_HELP'),
  },
  {
    key: 'total',
    label: t('CAMPAIGN.OPERATIONS.METRICS.TOTAL'),
    value: displayCount(deliverySummary.value.total),
  },
  {
    key: 'processable',
    label: t('CAMPAIGN.OPERATIONS.METRICS.PROCESSABLE'),
    value: displayCount(deliverySummary.value.processable_count),
  },
  {
    key: 'unknown',
    label: t('CAMPAIGN.OPERATIONS.METRICS.UNKNOWN'),
    value: displayCount(deliverySummary.value.unknown_count),
  },
]);

const backpressureDetails = computed(() => [
  {
    key: 'reason',
    label: t('CAMPAIGN.OPERATIONS.BACKPRESSURE.FIELDS.REASON'),
    value: displayValue(backpressure.value.reason || recovery.value.reason),
  },
  {
    key: 'provider',
    label: t('CAMPAIGN.OPERATIONS.BACKPRESSURE.FIELDS.PROVIDER'),
    value: displayValue(
      firstPresentValue([
        backpressure.value.provider,
        providerReadiness.value.provider,
        backpressure.value.channel,
        providerReadiness.value.channel,
      ])
    ),
  },
  {
    key: 'queue',
    label: t('CAMPAIGN.OPERATIONS.BACKPRESSURE.FIELDS.QUEUE'),
    value: displayValue(
      firstPresentValue([
        backpressure.value.queue,
        backpressure.value.queue_name,
        batchJob.value.queue,
        batchJob.value.queue_name,
        backpressure.value.resume_job_scheduled ? 'low' : null,
      ])
    ),
  },
  {
    key: 'scope',
    label: t('CAMPAIGN.OPERATIONS.BACKPRESSURE.FIELDS.SCOPE'),
    value: displayValue(backpressure.value.scope),
  },
  {
    key: 'limit',
    label: t('CAMPAIGN.OPERATIONS.BACKPRESSURE.FIELDS.LIMIT'),
    value: displayValue(backpressure.value.limit),
  },
  {
    key: 'observed',
    label: t('CAMPAIGN.OPERATIONS.BACKPRESSURE.FIELDS.OBSERVED'),
    value: displayValue(backpressure.value.observed_count),
  },
  {
    key: 'retry-after',
    label: t('CAMPAIGN.OPERATIONS.BACKPRESSURE.FIELDS.RETRY_AFTER'),
    value: displayValue(backpressure.value.retry_after_seconds),
  },
  {
    key: 'resume-job',
    label: t('CAMPAIGN.OPERATIONS.BACKPRESSURE.FIELDS.RESUME_JOB'),
    value: booleanDisplay(backpressure.value.resume_job_scheduled),
  },
]);

const promotionCriteria = computed(() =>
  objectValue(promotionReadiness.value.criteria)
);
const promotionDomains = computed(() =>
  Object.entries(objectValue(promotionReadiness.value.readiness)).map(
    ([key, value]) => ({
      key,
      status: value?.status,
      blockerCount: numberValue(
        firstPresentValue([
          value?.blocker_count,
          arrayValue(value?.blockers).length,
        ])
      ),
    })
  )
);
const promotionBlockers = computed(() =>
  arrayValue(promotionReadiness.value.blockers).slice(0, 5)
);

const promotionCriteriaItems = computed(() => [
  {
    key: 'min-pilot-contacts',
    label: t('CAMPAIGN.OPERATIONS.PROMOTION.FIELDS.MIN_PILOT_CONTACTS'),
    value: displayValue(promotionCriteria.value.min_pilot_contacts),
  },
  {
    key: 'external-evidence',
    label: t('CAMPAIGN.OPERATIONS.PROMOTION.FIELDS.EXTERNAL_EVIDENCE'),
    value: booleanDisplay(promotionCriteria.value.require_external_evidence),
  },
  {
    key: 'decision',
    label: t('CAMPAIGN.OPERATIONS.PROMOTION.FIELDS.DECISION'),
    value: displayValue(promotionCriteria.value.promotion_decision),
  },
  {
    key: 'blockers',
    label: t('CAMPAIGN.OPERATIONS.PROMOTION.FIELDS.BLOCKER_COUNT'),
    value: displayCount(promotionReadiness.value.blocker_count),
  },
]);

const promotionBlockerText = blocker =>
  [blocker.domain, blocker.code, blocker.action]
    .filter(hasValue)
    .map(String)
    .join(' / ');

const deliveryStatusCounts = computed(() =>
  objectValue(deliverySummary.value.by_status)
);

const deliveryStatusCount = status =>
  numberValue(deliveryStatusCounts.value[status]);

const hasTrackedLinks = computed(
  () => numberValue(trackedLinksSummary.value.total) > 0
);

const trackedLinkMetricItems = computed(() => [
  {
    key: 'total',
    label: t('CAMPAIGN.OPERATIONS.TRACKED_LINKS.TOTAL'),
    value: displayCount(trackedLinksSummary.value.total),
  },
  {
    key: 'contacts',
    label: t('CAMPAIGN.OPERATIONS.TRACKED_LINKS.CONTACTS'),
    value: displayCount(trackedLinksSummary.value.contacts_clicked),
  },
  {
    key: 'clicks',
    label: t('CAMPAIGN.OPERATIONS.TRACKED_LINKS.CLICKS'),
    value: displayCount(trackedLinksSummary.value.total_clicks),
  },
]);

const plannedDeliveryCount = computed(() =>
  numberValue(
    firstPresentValue([
      summary.value?.snapshot?.audience_count,
      campaignSummary.value.configured_audience_count,
      latestRunMetrics.value.persistent_deliveries,
      deliverySummary.value.total,
    ])
  )
);

const sentDeliveryCount = computed(() =>
  numberValue(
    firstPresentValue([
      latestRunMetrics.value.attempted,
      deliveryStatusCount('delivered') +
        deliveryStatusCount('failed') +
        deliveryStatusCount('unknown') +
        deliveryStatusCount('skipped') +
        deliveryStatusCount('suppressed'),
    ])
  )
);

const persistedDeliveryCount = computed(() =>
  numberValue(
    firstPresentValue([
      latestRunMetrics.value.persistent_deliveries,
      deliverySummary.value.total,
    ])
  )
);

const reconciliationCountItems = computed(() => [
  {
    key: 'planned',
    label: t('CAMPAIGN.OPERATIONS.RECONCILIATION.PLANNED'),
    value: displayCount(plannedDeliveryCount.value),
    tone: 'bg-n-alpha-2 text-n-slate-11',
  },
  {
    key: 'sent',
    label: t('CAMPAIGN.OPERATIONS.RECONCILIATION.SENT'),
    value: displayCount(sentDeliveryCount.value),
    tone: 'bg-n-blue-3 text-n-blue-11',
  },
  {
    key: 'delivered',
    label: t('CAMPAIGN.OPERATIONS.RECONCILIATION.DELIVERED'),
    value: displayCount(deliveryStatusCount('delivered')),
    tone: toneForStatus('delivered'),
  },
  {
    key: 'failed',
    label: t('CAMPAIGN.OPERATIONS.RECONCILIATION.FAILED'),
    value: displayCount(deliveryStatusCount('failed')),
    tone: toneForStatus('failed'),
  },
  {
    key: 'unknown',
    label: t('CAMPAIGN.OPERATIONS.RECONCILIATION.UNKNOWN'),
    value: displayCount(deliverySummary.value.unknown_count),
    tone: toneForStatus('unknown'),
  },
  {
    key: 'pending',
    label: t('CAMPAIGN.OPERATIONS.RECONCILIATION.PENDING'),
    value: displayCount(deliveryStatusCount('pending')),
    tone: toneForStatus('pending'),
  },
]);

const reconciliationDelta = computed(
  () => plannedDeliveryCount.value - persistedDeliveryCount.value
);

const deliveryReconciliationDetails = computed(() => [
  {
    key: 'delivery',
    label: t('CAMPAIGN.OPERATIONS.RECONCILIATION.LAST_DELIVERY'),
    value: displayValue(deliveryReconciliation.value.campaign_delivery_id),
  },
  {
    key: 'state',
    label: t('CAMPAIGN.OPERATIONS.RECONCILIATION.LAST_STATE'),
    value: displayValue(deliveryReconciliation.value.state),
  },
  {
    key: 'reviewer',
    label: t('CAMPAIGN.OPERATIONS.RECONCILIATION.REVIEWER'),
    value: displayValue(deliveryReconciliation.value.reviewer_id),
  },
  {
    key: 'retry',
    label: t('CAMPAIGN.OPERATIONS.RECONCILIATION.RETRY_REQUESTED'),
    value: booleanDisplay(deliveryReconciliation.value.retry_requested),
  },
]);

const runDetails = computed(() => [
  {
    key: 'worker',
    label: t('CAMPAIGN.OPERATIONS.RUN.WORKER'),
    value: displayValue(latestRun.value.worker),
  },
  {
    key: 'attempt',
    label: t('CAMPAIGN.OPERATIONS.RUN.ATTEMPT'),
    value: displayValue(latestRun.value.attempt_number),
  },
  {
    key: 'cursor',
    label: t('CAMPAIGN.OPERATIONS.RUN.CURSOR'),
    value: displayValue(cursor.value.state),
  },
  {
    key: 'processed',
    label: t('CAMPAIGN.OPERATIONS.RUN.PROCESSED'),
    value: displayValue(cursor.value.processed_count),
  },
  {
    key: 'batch',
    label: t('CAMPAIGN.OPERATIONS.RUN.BATCH'),
    value: displayValue(batchJob.value.batch_index ?? cursor.value.batch_index),
  },
  {
    key: 'heartbeat',
    label: t('CAMPAIGN.OPERATIONS.RUN.HEARTBEAT'),
    value: formatDate(latestRun.value.heartbeat_at),
  },
]);

const deliveryStatusItems = computed(() =>
  Object.entries(deliverySummary.value.by_status || {}).map(
    ([status, count]) => ({
      status,
      count,
      tone: toneForStatus(status),
    })
  )
);

const receiptStateItems = computed(() =>
  Object.entries(receiptSummary.value.latest_by_state || {})
);

const receiptSourceItems = computed(() =>
  Object.entries(receiptSummary.value.latest_by_source || {})
);

const latestUnknownDeliveries = computed(
  () => deliverySummary.value.latest_unknown || []
);

const approvalStages = computed(() =>
  Array.isArray(approval.value.stages) ? approval.value.stages : []
);

const approvalHistory = computed(() =>
  Array.isArray(approval.value.history)
    ? [...approval.value.history].reverse().slice(0, 5)
    : []
);

const isApprovalInReview = computed(
  () =>
    summary.value?.campaign?.status === IN_REVIEW_STATUS ||
    approval.value.status === IN_REVIEW_STATUS
);

const canManageApprovalWorkflow = computed(
  () => permissions.value.approval_workflow !== false
);

const canManageCanaryApproval = computed(
  () => permissions.value.approve_canary !== false
);

const canManageDeliveryReconciliation = computed(
  () => permissions.value.reconcile_delivery !== false
);

const canApproveCanary = computed(
  () =>
    canManageCanaryApproval.value &&
    canary.value.enabled === true &&
    canary.value.approved_to_continue !== true &&
    summary.value?.campaign?.status === 'paused' &&
    latestRun.value.status === 'paused' &&
    canary.value.last_reason === CANARY_LIMIT_REASON
);

const deliveryHasReconcilableAttempt = delivery =>
  hasValue(delivery.delivery_attempt_id);

const canReconcileDelivery = delivery =>
  canManageDeliveryReconciliation.value &&
  deliveryHasReconcilableAttempt(delivery);

const canRetryDeliveryAfterReconciliation = delivery =>
  canReconcileDelivery(delivery) &&
  summary.value?.campaign?.status !== 'cancelled' &&
  !isProviderReadinessBlocking.value;

const canReconcileDeliveries = computed(() =>
  latestUnknownDeliveries.value.some(canReconcileDelivery)
);

const hasReconciliationActions = computed(() =>
  latestUnknownDeliveries.value.some(
    delivery =>
      canReconcileDelivery(delivery) ||
      canRetryDeliveryAfterReconciliation(delivery)
  )
);

const hasProviderBlockedRetry = computed(
  () =>
    canManageDeliveryReconciliation.value &&
    latestUnknownDeliveries.value.some(deliveryHasReconcilableAttempt) &&
    isProviderReadinessBlocking.value
);

const canSubmitReview = computed(() => {
  const campaignStatus = summary.value?.campaign?.status;
  const approvalStatus = approval.value.status;

  return (
    canManageApprovalWorkflow.value &&
    campaignStatus === 'draft' &&
    approvalStatus !== IN_REVIEW_STATUS &&
    approvalStatus !== APPROVED_STATUS
  );
});

const canApproveStage = stage =>
  canManageApprovalWorkflow.value &&
  isApprovalInReview.value &&
  stage.status !== APPROVED_STATUS;

const approvalActionKey = (approvalAction, stageKey = '') =>
  [approvalAction, stageKey].filter(Boolean).join(':');

const isApprovalActionLoading = (approvalAction, stageKey = '') =>
  approvalWorkflowUIFlags.value.isUpdating &&
  activeApprovalAction.value === approvalActionKey(approvalAction, stageKey);

const reconciliationActionKey = (deliveryId, state) => `${deliveryId}:${state}`;

const isReconciliationActionLoading = (deliveryId, state) =>
  deliveryReconciliationUIFlags.value.isUpdating &&
  activeReconciliationAction.value ===
    reconciliationActionKey(deliveryId, state);

const approvalEventText = event =>
  [event.event, event.stage_key, event.reason]
    .filter(hasValue)
    .map(String)
    .join(' / ');

const loadSummary = async () => {
  if (!props.campaignId) return;

  try {
    await store.dispatch('campaigns/getOperationsSummary', props.campaignId);
  } catch {
    // The store owns the visible error state for this operational surface.
  }
};

const runApprovalWorkflow = async (approvalAction, stageKey = null) => {
  activeApprovalAction.value = approvalActionKey(approvalAction, stageKey);

  try {
    await store.dispatch('campaigns/runApprovalWorkflow', {
      campaignId: props.campaignId,
      approvalAction,
      stageKey,
      reason: approvalReason.value,
    });
    approvalReason.value = '';
    await loadSummary();
  } catch {
    // The store owns the visible error state for approval actions.
  } finally {
    activeApprovalAction.value = null;
  }
};

const requestChanges = stageKey => {
  if (!approvalReason.value.trim()) {
    pendingRequestChangesStageKey.value = stageKey;
    return;
  }

  runApprovalWorkflow('request_changes', stageKey);
};

const confirmRequestChangesWithoutReason = () => {
  const stageKey = pendingRequestChangesStageKey.value;
  pendingRequestChangesStageKey.value = null;
  runApprovalWorkflow('request_changes', stageKey);
};

const approveCanary = async () => {
  try {
    await store.dispatch('campaigns/approveCanary', {
      campaignId: props.campaignId,
      reason: canaryReason.value,
    });
    canaryReason.value = '';
    await loadSummary();
  } catch {
    // The store owns the visible error state for canary actions.
  }
};

const reconcileDelivery = async (delivery, state) => {
  activeReconciliationAction.value = reconciliationActionKey(
    delivery.id,
    state
  );

  try {
    await store.dispatch('campaigns/reconcileDelivery', {
      campaignId: props.campaignId,
      deliveryId: delivery.id,
      state,
      providerMessageId: delivery.provider_message_id,
      failureClassification: state === 'FAILED' ? 'RETRYABLE' : null,
      errorCode:
        state === 'FAILED' ? MANUAL_FAILED_RECONCILIATION_ERROR_CODE : null,
      reason: reconciliationReason.value,
      retryAfterReconciliation: state === 'FAILED',
      evidence: {
        source: 'campaign_operations_panel',
        campaign_delivery_id: delivery.id,
      },
    });
    reconciliationReason.value = '';
    await loadSummary();
  } catch {
    // The store owns the visible error state for reconciliation actions.
  } finally {
    activeReconciliationAction.value = null;
  }
};

onMounted(loadSummary);
watch(campaignId, loadSummary);
</script>

<template>
  <section
    class="px-6 py-5 border-t bg-n-alpha-1 border-n-weak"
    data-test="campaign-operations-summary"
  >
    <header class="flex flex-wrap items-center justify-between gap-3">
      <div class="min-w-0">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.OPERATIONS.TITLE') }}
        </h3>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.OPERATIONS.SUBTITLE') }}
        </p>
      </div>
      <Button
        v-tooltip.top="t('CAMPAIGN.OPERATIONS.ACTIONS.REFRESH')"
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-refresh-cw"
        :is-loading="uiFlags.isFetching"
        :aria-label="t('CAMPAIGN.OPERATIONS.ACTIONS.REFRESH')"
        :title="t('CAMPAIGN.OPERATIONS.ACTIONS.REFRESH')"
        @click="loadSummary"
      />
    </header>

    <div
      v-if="isInitialLoading"
      class="flex items-center gap-2 py-8 text-sm text-n-slate-11"
    >
      <Spinner />
      <span>{{ t('CAMPAIGN.OPERATIONS.LOADING') }}</span>
    </div>

    <div
      v-else-if="uiFlags.hasError && !summary"
      class="flex flex-wrap items-center justify-between gap-3 py-6"
    >
      <span class="text-sm text-n-ruby-11">
        {{ t('CAMPAIGN.OPERATIONS.ERROR') }}
      </span>
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-refresh-cw"
        :label="t('CAMPAIGN.OPERATIONS.ACTIONS.RETRY')"
        @click="loadSummary"
      />
    </div>

    <div v-else-if="!summary" class="py-6 text-sm text-n-slate-11">
      {{ t('CAMPAIGN.OPERATIONS.EMPTY') }}
    </div>

    <div v-else class="mt-5 space-y-5">
      <div
        v-if="uiFlags.hasError"
        class="rounded-md bg-n-ruby-3 px-3 py-2 text-xs text-n-ruby-11"
      >
        {{ t('CAMPAIGN.OPERATIONS.REFRESH_ERROR') }}
      </div>

      <dl class="grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4">
        <div
          v-for="metric in metricItems"
          :key="metric.key"
          class="min-w-0"
          :title="metric.title || metric.value"
        >
          <dt class="text-xs font-medium text-n-slate-11">
            {{ metric.label }}
          </dt>
          <dd class="mt-1 text-xl font-semibold text-n-slate-12 tabular-nums">
            {{ metric.value }}
          </dd>
        </div>
      </dl>

      <div
        class="grid grid-cols-1 gap-3 border-t border-n-weak pt-4 md:grid-cols-6"
      >
        <div v-for="item in statusItems" :key="item.key" class="min-w-0">
          <div class="text-xs font-medium text-n-slate-11">
            {{ item.label }}
          </div>
          <span
            class="mt-1 inline-flex max-w-full items-center rounded-md px-2 py-1 text-xs font-medium"
            :class="item.tone"
            :title="item.title || item.value"
          >
            <span class="truncate">{{ item.value }}</span>
          </span>
        </div>
      </div>

      <section class="border-t border-n-weak pt-4" data-test="operations-gate">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="min-w-0">
            <h4 class="text-xs font-semibold uppercase text-n-slate-11">
              {{ t('CAMPAIGN.OPERATIONS.GATE.TITLE') }}
            </h4>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ operationalStateDescription }}
            </p>
          </div>
          <span
            class="inline-flex max-w-full items-center rounded-md px-2 py-1 text-xs font-medium"
            :class="toneForStatus(operationalState)"
            data-test="operations-gate-status"
          >
            <span class="truncate">{{ operationalStateLabel }}</span>
          </span>
        </div>

        <dl class="mt-3 grid grid-cols-1 gap-2 md:grid-cols-4">
          <div
            v-for="item in operationalSignalItems"
            :key="item.key"
            class="flex min-w-0 items-center justify-between gap-3 rounded-md bg-n-alpha-1 px-3 py-2"
          >
            <dt class="truncate text-xs font-medium text-n-slate-11">
              {{ item.label }}
            </dt>
            <dd
              class="inline-flex max-w-[160px] shrink-0 items-center rounded-md px-2 py-1 text-xs font-medium"
              :class="item.tone"
              :title="item.value"
            >
              <span class="truncate">{{ item.value }}</span>
            </dd>
          </div>
        </dl>
      </section>

      <section
        v-if="hasBackpressureSignal"
        class="border-t border-n-weak pt-4"
        data-test="backpressure-summary"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h4 class="text-xs font-semibold uppercase text-n-slate-11">
            {{ t('CAMPAIGN.OPERATIONS.BACKPRESSURE.TITLE') }}
          </h4>
          <span
            class="inline-flex max-w-full items-center rounded-md bg-n-amber-3 px-2 py-1 text-xs font-medium text-n-amber-11"
          >
            <span class="truncate">
              {{ t('CAMPAIGN.OPERATIONS.GATE.STATUS.DEGRADED') }}
            </span>
          </span>
        </div>

        <dl class="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-4">
          <div
            v-for="item in backpressureDetails"
            :key="item.key"
            class="min-w-0"
          >
            <dt class="text-xs text-n-slate-11">{{ item.label }}</dt>
            <dd class="mt-0.5 truncate text-sm text-n-slate-12">
              {{ item.value }}
            </dd>
          </div>
        </dl>
      </section>

      <section
        v-if="hasProviderReadiness"
        class="border-t border-n-weak pt-4"
        data-test="provider-readiness"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h4 class="text-xs font-semibold uppercase text-n-slate-11">
            {{ t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.TITLE') }}
          </h4>
          <span
            class="inline-flex max-w-full items-center rounded-md px-2 py-1 text-xs font-medium"
            :class="providerReadinessTone"
          >
            <span class="truncate">{{ providerReadinessStatusLabel }}</span>
          </span>
        </div>

        <div
          v-if="isProviderReadinessBlockedRecovery"
          class="mt-3 rounded-md bg-n-ruby-3 px-3 py-2 text-xs text-n-ruby-11"
          data-test="provider-readiness-recovery"
        >
          <div class="font-medium">
            {{ t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.RECOVERY_TITLE') }}
          </div>
          <div class="mt-1">
            {{
              t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.RECOVERY_DESCRIPTION')
            }}
          </div>
          <div class="mt-1">
            {{
              t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.RECOMMENDED_ACTION', {
                action: displayValue(recovery.recommended_action),
              })
            }}
          </div>
        </div>

        <dl class="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-3">
          <div
            v-for="item in providerReadinessDetails"
            :key="item.key"
            class="min-w-0"
          >
            <dt class="text-xs text-n-slate-11">{{ item.label }}</dt>
            <dd class="mt-0.5 truncate text-sm text-n-slate-12">
              {{ item.value }}
            </dd>
          </div>
        </dl>

        <div
          v-if="providerReadinessChecks.length"
          class="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2"
          data-test="provider-readiness-checks"
        >
          <div
            v-for="check in providerReadinessChecks"
            :key="check.key"
            class="flex min-w-0 items-center justify-between gap-3 rounded-md bg-n-alpha-1 px-3 py-2"
          >
            <span class="truncate text-xs font-medium text-n-slate-12">
              {{ providerReadinessCheckLabel(check) }}
            </span>
            <span
              class="inline-flex max-w-[140px] shrink-0 items-center rounded-md px-2 py-1 text-xs font-medium"
              :class="toneForStatus(check.status)"
              :title="displayValue(check.status)"
            >
              <span class="truncate">{{ displayValue(check.status) }}</span>
            </span>
          </div>
        </div>

        <div
          v-if="hasProviderCredentialCheck"
          class="mt-4 rounded-md bg-n-alpha-1 px-3 py-3"
          data-test="provider-readiness-credentials"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <h5 class="text-xs font-semibold uppercase text-n-slate-11">
              {{
                t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.CREDENTIALS.TITLE')
              }}
            </h5>
            <span
              class="inline-flex max-w-full items-center rounded-md px-2 py-1 text-xs font-medium"
              :class="toneForStatus(providerCredentialCheck.status)"
            >
              <span class="truncate">
                {{ displayValue(providerCredentialCheck.status) }}
              </span>
            </span>
          </div>

          <dl class="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
            <div class="min-w-0">
              <dt class="text-xs text-n-slate-11">
                {{
                  t(
                    'CAMPAIGN.OPERATIONS.PROVIDER_READINESS.CREDENTIALS.SENDER_REFERENCE'
                  )
                }}
              </dt>
              <dd class="mt-0.5 truncate text-sm text-n-slate-12">
                {{
                  booleanDisplay(
                    providerCredentialCheck.sender_reference_present
                  )
                }}
              </dd>
            </div>
            <div class="min-w-0">
              <dt class="text-xs text-n-slate-11">
                {{
                  t(
                    'CAMPAIGN.OPERATIONS.PROVIDER_READINESS.CREDENTIALS.REQUIRED_COUNT'
                  )
                }}
              </dt>
              <dd class="mt-0.5 truncate text-sm text-n-slate-12">
                {{
                  displayCount(
                    arrayValue(providerCredentialCheck.required_keys).length
                  )
                }}
              </dd>
            </div>
          </dl>

          <div v-if="hasProviderCredentialKeyDetails" class="mt-3 space-y-2">
            <div
              v-for="group in providerCredentialKeyGroups"
              :key="group.key"
              class="flex flex-wrap items-start gap-2"
            >
              <span class="min-w-[88px] text-xs font-medium text-n-slate-11">
                {{ group.label }}
              </span>
              <span v-if="!group.values.length" class="text-xs text-n-slate-11">
                {{ EMPTY_VALUE }}
              </span>
              <span
                v-for="value in group.values"
                :key="`${group.key}-${value}`"
                class="inline-flex max-w-full items-center rounded-md px-2 py-1 text-xs font-medium"
                :class="group.tone"
              >
                <span class="truncate">{{ value }}</span>
              </span>
            </div>
          </div>
        </div>

        <div
          class="mt-4 rounded-md bg-n-alpha-1 px-3 py-3"
          data-test="provider-readiness-template"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <h5 class="text-xs font-semibold uppercase text-n-slate-11">
              {{ t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.TEMPLATE.TITLE') }}
            </h5>
            <span
              class="inline-flex max-w-full items-center rounded-md px-2 py-1 text-xs font-medium"
              :class="
                hasWhatsappTemplateCheck
                  ? toneForStatus(whatsappTemplateCheck.status)
                  : 'bg-n-alpha-2 text-n-slate-11'
              "
            >
              <span class="truncate">
                {{
                  hasWhatsappTemplateCheck
                    ? displayValue(whatsappTemplateCheck.status)
                    : t(
                        'CAMPAIGN.OPERATIONS.PROVIDER_READINESS.TEMPLATE.NOT_REQUIRED'
                      )
                }}
              </span>
            </span>
          </div>

          <dl
            v-if="hasWhatsappTemplateCheck"
            class="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2"
          >
            <div class="min-w-0">
              <dt class="text-xs text-n-slate-11">
                {{ t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.TEMPLATE.NAME') }}
              </dt>
              <dd class="mt-0.5 truncate text-sm text-n-slate-12">
                {{ displayValue(whatsappTemplateCheck.template_name) }}
              </dd>
            </div>
            <div class="min-w-0">
              <dt class="text-xs text-n-slate-11">
                {{
                  t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.TEMPLATE.LANGUAGE')
                }}
              </dt>
              <dd class="mt-0.5 truncate text-sm text-n-slate-12">
                {{ displayValue(whatsappTemplateCheck.language) }}
              </dd>
            </div>
          </dl>
        </div>

        <div
          v-if="providerReadinessBlockers.length"
          class="mt-4 rounded-md bg-n-ruby-3 px-3 py-3 text-xs text-n-ruby-11"
          data-test="provider-readiness-blockers"
        >
          <h5 class="font-semibold uppercase">
            {{ t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.BLOCKERS.TITLE') }}
          </h5>
          <ul class="mt-2 space-y-1">
            <li
              v-for="blocker in providerReadinessBlockers"
              :key="blocker"
              class="flex gap-2"
            >
              <span
                aria-hidden="true"
                class="i-lucide-circle-alert mt-0.5 size-3 shrink-0"
              />
              <span>{{ providerReadinessBlockerLabel(blocker) }}</span>
            </li>
          </ul>
        </div>

        <div
          v-if="hasProviderReadinessLatestBlock"
          class="mt-4 rounded-md bg-n-alpha-1 px-3 py-3"
          data-test="provider-readiness-latest-block"
        >
          <h5 class="text-xs font-semibold uppercase text-n-slate-11">
            {{ t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.LATEST_BLOCK') }}
          </h5>
          <dl class="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-3">
            <div
              v-for="item in providerReadinessLatestBlockDetails"
              :key="item.key"
              class="min-w-0"
            >
              <dt class="text-xs text-n-slate-11">{{ item.label }}</dt>
              <dd class="mt-0.5 truncate text-sm text-n-slate-12">
                {{ item.value }}
              </dd>
            </div>
          </dl>
        </div>

        <p class="mt-3 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.OPERATIONS.PROVIDER_READINESS.REDACTION_NOTICE') }}
        </p>
      </section>

      <section class="border-t border-n-weak pt-4">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h4 class="text-xs font-semibold uppercase text-n-slate-11">
            {{ t('CAMPAIGN.OPERATIONS.APPROVAL.TITLE') }}
          </h4>
          <Button
            v-if="canSubmitReview"
            variant="faded"
            color="blue"
            size="sm"
            icon="i-lucide-send"
            :label="t('CAMPAIGN.OPERATIONS.APPROVAL.SUBMIT')"
            :is-loading="isApprovalActionLoading('submit_review')"
            data-test="approval-submit-review"
            @click="runApprovalWorkflow('submit_review')"
          />
        </div>

        <div
          v-if="approvalWorkflowUIFlags.hasError"
          class="mt-3 rounded-md bg-n-ruby-3 px-3 py-2 text-xs text-n-ruby-11"
        >
          {{ t('CAMPAIGN.OPERATIONS.APPROVAL.ACTION_ERROR') }}
        </div>

        <div v-if="canManageApprovalWorkflow" class="mt-3">
          <label
            class="mb-1 block text-xs font-medium text-n-slate-11"
            :for="`campaign-approval-reason-${campaignId}`"
          >
            {{ t('CAMPAIGN.OPERATIONS.APPROVAL.REASON') }}
          </label>
          <input
            :id="`campaign-approval-reason-${campaignId}`"
            v-model="approvalReason"
            class="h-9 w-full rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-10 focus:border-n-brand"
            type="text"
            :placeholder="t('CAMPAIGN.OPERATIONS.APPROVAL.REASON_PLACEHOLDER')"
            data-test="approval-reason"
          />
        </div>

        <div v-if="approvalStages.length" class="mt-4 divide-y divide-n-weak">
          <div
            v-if="pendingRequestChangesStageKey"
            class="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-md bg-n-amber-3 px-3 py-2 text-xs text-n-amber-11"
            data-test="approval-empty-reason-confirmation"
          >
            <span>
              {{ t('CAMPAIGN.OPERATIONS.APPROVAL.EMPTY_REASON_CONFIRMATION') }}
            </span>
            <div class="flex gap-2">
              <Button
                variant="faded"
                color="amber"
                size="xs"
                :label="
                  t('CAMPAIGN.OPERATIONS.APPROVAL.CONFIRM_WITHOUT_REASON')
                "
                data-test="approval-confirm-empty-reason"
                @click="confirmRequestChangesWithoutReason"
              />
              <Button
                variant="ghost"
                color="slate"
                size="xs"
                :label="t('CAMPAIGN.OPERATIONS.APPROVAL.CANCEL')"
                data-test="approval-cancel-empty-reason"
                @click="pendingRequestChangesStageKey = null"
              />
            </div>
          </div>

          <div
            v-for="stage in approvalStages"
            :key="stage.key"
            class="flex flex-wrap items-center justify-between gap-3 py-3"
          >
            <div class="min-w-0">
              <div class="truncate text-sm font-medium text-n-slate-12">
                {{ displayValue(stage.label || stage.key) }}
              </div>
              <span
                class="mt-1 inline-flex max-w-full items-center rounded-md px-2 py-1 text-xs font-medium"
                :class="toneForStatus(stage.status)"
              >
                <span class="truncate">{{ displayValue(stage.status) }}</span>
              </span>
            </div>
            <div
              v-if="isApprovalInReview && canManageApprovalWorkflow"
              class="flex shrink-0 flex-wrap justify-end gap-2"
            >
              <Button
                v-if="canApproveStage(stage)"
                variant="faded"
                color="teal"
                size="sm"
                icon="i-lucide-check"
                :label="t('CAMPAIGN.OPERATIONS.APPROVAL.APPROVE_STAGE')"
                :is-loading="
                  isApprovalActionLoading('approve_stage', stage.key)
                "
                :data-test="`approval-approve-${stage.key}`"
                @click="runApprovalWorkflow('approve_stage', stage.key)"
              />
              <Button
                variant="faded"
                color="amber"
                size="sm"
                icon="i-lucide-message-square-warning"
                :label="t('CAMPAIGN.OPERATIONS.APPROVAL.REQUEST_CHANGES')"
                :is-loading="
                  isApprovalActionLoading('request_changes', stage.key)
                "
                :data-test="`approval-request-changes-${stage.key}`"
                @click="requestChanges(stage.key)"
              />
            </div>
          </div>
        </div>

        <div v-if="approvalHistory.length" class="mt-4">
          <h5 class="text-xs font-semibold uppercase text-n-slate-11">
            {{ t('CAMPAIGN.OPERATIONS.APPROVAL.HISTORY') }}
          </h5>
          <ol class="mt-2 space-y-2">
            <li
              v-for="event in approvalHistory"
              :key="`${event.event}-${event.at}-${event.stage_key || ''}`"
              class="grid grid-cols-1 gap-1 text-xs text-n-slate-11 md:grid-cols-[minmax(0,1fr)_auto]"
            >
              <span class="min-w-0 truncate text-n-slate-12">
                {{ approvalEventText(event) }}
              </span>
              <span class="whitespace-nowrap">
                {{ formatDate(event.at) }}
              </span>
            </li>
          </ol>
        </div>
      </section>

      <section class="border-t border-n-weak pt-4">
        <h4 class="text-xs font-semibold uppercase text-n-slate-11">
          {{ t('CAMPAIGN.OPERATIONS.RUN.TITLE') }}
        </h4>
        <dl class="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-3">
          <div v-for="item in runDetails" :key="item.key" class="min-w-0">
            <dt class="text-xs text-n-slate-11">{{ item.label }}</dt>
            <dd class="mt-0.5 truncate text-sm text-n-slate-12">
              {{ item.value }}
            </dd>
          </div>
        </dl>
      </section>

      <section v-if="canary.enabled" class="border-t border-n-weak pt-4">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h4 class="text-xs font-semibold uppercase text-n-slate-11">
            {{ t('CAMPAIGN.OPERATIONS.CANARY.TITLE') }}
          </h4>
          <Button
            v-if="canApproveCanary"
            variant="faded"
            color="blue"
            size="sm"
            icon="i-lucide-play"
            :label="t('CAMPAIGN.OPERATIONS.CANARY.APPROVE')"
            :is-loading="canaryApprovalUIFlags.isUpdating"
            data-test="canary-approve"
            @click="approveCanary"
          />
        </div>

        <div
          v-if="canaryApprovalUIFlags.hasError"
          class="mt-3 rounded-md bg-n-ruby-3 px-3 py-2 text-xs text-n-ruby-11"
        >
          {{ t('CAMPAIGN.OPERATIONS.CANARY.ACTION_ERROR') }}
        </div>

        <dl class="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-4">
          <div class="min-w-0">
            <dt class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.OPERATIONS.CANARY.LIMIT') }}
            </dt>
            <dd class="mt-0.5 truncate text-sm text-n-slate-12">
              {{ displayValue(canary.limit) }}
            </dd>
          </div>
          <div class="min-w-0">
            <dt class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.OPERATIONS.CANARY.DELIVERED') }}
            </dt>
            <dd class="mt-0.5 truncate text-sm text-n-slate-12">
              {{ displayValue(canary.delivered_count) }}
            </dd>
          </div>
          <div class="min-w-0">
            <dt class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.OPERATIONS.CANARY.PENDING') }}
            </dt>
            <dd class="mt-0.5 truncate text-sm text-n-slate-12">
              {{ displayValue(canary.pending_count) }}
            </dd>
          </div>
          <div class="min-w-0">
            <dt class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.OPERATIONS.CANARY.LAST_REASON') }}
            </dt>
            <dd class="mt-0.5 truncate text-sm text-n-slate-12">
              {{ displayValue(canary.last_reason) }}
            </dd>
          </div>
        </dl>

        <div v-if="canApproveCanary" class="mt-3">
          <label
            class="mb-1 block text-xs font-medium text-n-slate-11"
            :for="`campaign-canary-reason-${campaignId}`"
          >
            {{ t('CAMPAIGN.OPERATIONS.CANARY.REASON') }}
          </label>
          <input
            :id="`campaign-canary-reason-${campaignId}`"
            v-model="canaryReason"
            class="h-9 w-full rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-10 focus:border-n-brand"
            type="text"
            :placeholder="t('CAMPAIGN.OPERATIONS.CANARY.REASON_PLACEHOLDER')"
            data-test="canary-reason"
          />
        </div>
      </section>

      <section
        v-if="hasPromotionReadiness"
        class="border-t border-n-weak pt-4"
        data-test="promotion-readiness"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h4 class="text-xs font-semibold uppercase text-n-slate-11">
            {{ t('CAMPAIGN.OPERATIONS.PROMOTION.TITLE') }}
          </h4>
          <span
            class="inline-flex max-w-full items-center rounded-md px-2 py-1 text-xs font-medium"
            :class="toneForStatus(promotionReadiness.status)"
            data-test="promotion-readiness-status"
          >
            <span class="truncate">
              {{ displayValue(promotionReadiness.status) }}
            </span>
          </span>
        </div>

        <dl class="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-4">
          <div
            v-for="item in promotionCriteriaItems"
            :key="item.key"
            class="min-w-0"
          >
            <dt class="text-xs text-n-slate-11">{{ item.label }}</dt>
            <dd class="mt-0.5 truncate text-sm text-n-slate-12">
              {{ item.value }}
            </dd>
          </div>
        </dl>

        <div
          v-if="promotionDomains.length"
          class="mt-4 flex flex-wrap gap-2"
          data-test="promotion-domains"
        >
          <span
            v-for="domain in promotionDomains"
            :key="domain.key"
            class="inline-flex max-w-full items-center gap-1 rounded-md px-2 py-1 text-xs font-medium"
            :class="toneForStatus(domain.status)"
            :title="`${domain.key}: ${displayValue(domain.status)}`"
          >
            <span class="truncate">{{ domain.key }}</span>
            <span class="tabular-nums">{{ domain.blockerCount }}</span>
          </span>
        </div>

        <ul
          v-if="promotionBlockers.length"
          class="mt-4 space-y-1 rounded-md bg-n-ruby-3 px-3 py-3 text-xs text-n-ruby-11"
          data-test="promotion-blockers"
        >
          <li
            v-for="blocker in promotionBlockers"
            :key="`${blocker.domain}-${blocker.code}`"
            class="flex gap-2"
          >
            <span
              aria-hidden="true"
              class="i-lucide-circle-alert mt-0.5 size-3 shrink-0"
            />
            <span>{{ promotionBlockerText(blocker) }}</span>
          </li>
        </ul>
      </section>

      <section
        class="border-t border-n-weak pt-4"
        data-test="delivery-reconciliation-summary"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="min-w-0">
            <h4 class="text-xs font-semibold uppercase text-n-slate-11">
              {{ t('CAMPAIGN.OPERATIONS.RECONCILIATION.TITLE') }}
            </h4>
            <p
              v-if="reconciliationDelta !== 0"
              class="mt-1 text-xs text-n-amber-11"
              data-test="delivery-reconciliation-delta"
            >
              {{
                t('CAMPAIGN.OPERATIONS.RECONCILIATION.DELTA', {
                  count: reconciliationDelta,
                })
              }}
            </p>
          </div>
          <span
            class="inline-flex max-w-full items-center rounded-md px-2 py-1 text-xs font-medium"
            :class="
              isDeliveryReconciliationActive
                ? toneForStatus('reconciling')
                : toneForStatus('ready')
            "
          >
            <span class="truncate">
              {{
                isDeliveryReconciliationActive
                  ? t('CAMPAIGN.OPERATIONS.GATE.STATUS.RECONCILING')
                  : t('CAMPAIGN.OPERATIONS.GATE.STATUS.READY')
              }}
            </span>
          </span>
        </div>

        <dl class="mt-3 grid grid-cols-2 gap-2 md:grid-cols-6">
          <div
            v-for="item in reconciliationCountItems"
            :key="item.key"
            class="min-w-0 rounded-md bg-n-alpha-1 px-3 py-2"
          >
            <dt class="truncate text-xs text-n-slate-11">
              {{ item.label }}
            </dt>
            <dd
              class="mt-1 inline-flex max-w-full items-center rounded-md px-2 py-1 text-sm font-semibold tabular-nums"
              :class="item.tone"
            >
              {{ item.value }}
            </dd>
          </div>
        </dl>

        <dl
          v-if="hasDeliveryReconciliationMetadata"
          class="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-4"
          data-test="delivery-reconciliation-last-action"
        >
          <div
            v-for="item in deliveryReconciliationDetails"
            :key="item.key"
            class="min-w-0"
          >
            <dt class="text-xs text-n-slate-11">{{ item.label }}</dt>
            <dd class="mt-0.5 truncate text-sm text-n-slate-12">
              {{ item.value }}
            </dd>
          </div>
        </dl>
      </section>

      <section
        v-if="deliveryStatusItems.length || receiptStateItems.length"
        class="grid grid-cols-1 gap-5 border-t border-n-weak pt-4 lg:grid-cols-3"
      >
        <div v-if="deliveryStatusItems.length" class="min-w-0">
          <h4 class="text-xs font-semibold uppercase text-n-slate-11">
            {{ t('CAMPAIGN.OPERATIONS.DELIVERIES.TITLE') }}
          </h4>
          <div class="mt-3 flex flex-wrap gap-2">
            <span
              v-for="item in deliveryStatusItems"
              :key="item.status"
              class="inline-flex max-w-full items-center gap-1 rounded-md px-2 py-1 text-xs font-medium"
              :class="item.tone"
            >
              <span class="truncate">{{ item.status }}</span>
              <span class="tabular-nums">{{ item.count }}</span>
            </span>
          </div>
        </div>

        <div v-if="receiptStateItems.length" class="min-w-0">
          <h4 class="text-xs font-semibold uppercase text-n-slate-11">
            {{ t('CAMPAIGN.OPERATIONS.RECEIPTS.STATE') }}
          </h4>
          <div class="mt-3 flex flex-wrap gap-2">
            <span
              v-for="[state, count] in receiptStateItems"
              :key="state"
              class="inline-flex max-w-full items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11"
            >
              <span class="truncate">{{ state }}</span>
              <span class="tabular-nums">{{ count }}</span>
            </span>
          </div>
        </div>

        <div v-if="receiptSourceItems.length" class="min-w-0">
          <h4 class="text-xs font-semibold uppercase text-n-slate-11">
            {{ t('CAMPAIGN.OPERATIONS.RECEIPTS.SOURCE') }}
          </h4>
          <div class="mt-3 flex flex-wrap gap-2">
            <span
              v-for="[source, count] in receiptSourceItems"
              :key="source"
              class="inline-flex max-w-full items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11"
            >
              <span class="truncate">{{ source }}</span>
              <span class="tabular-nums">{{ count }}</span>
            </span>
          </div>
        </div>
      </section>

      <section
        v-if="hasTrackedLinks"
        class="border-t border-n-weak pt-4"
        data-test="campaign-tracked-links"
      >
        <h4 class="text-xs font-semibold uppercase text-n-slate-11">
          {{ t('CAMPAIGN.OPERATIONS.TRACKED_LINKS.TITLE') }}
        </h4>
        <dl class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div v-for="item in trackedLinkMetricItems" :key="item.key">
            <dt class="text-xs text-n-slate-11">{{ item.label }}</dt>
            <dd class="mt-0.5 text-sm font-medium text-n-slate-12">
              {{ item.value }}
            </dd>
          </div>
        </dl>

        <div v-if="latestTrackedLinkClicks.length" class="mt-4 overflow-x-auto">
          <h5 class="text-xs font-medium text-n-slate-11">
            {{ t('CAMPAIGN.OPERATIONS.TRACKED_LINKS.LATEST') }}
          </h5>
          <table class="mt-2 w-full min-w-[700px] text-left text-xs">
            <thead class="text-n-slate-11">
              <tr class="[&>th]:pb-2 [&>th]:font-medium">
                <th>{{ t('CAMPAIGN.OPERATIONS.TRACKED_LINKS.CONTACT') }}</th>
                <th>{{ t('CAMPAIGN.OPERATIONS.TRACKED_LINKS.URL') }}</th>
                <th>{{ t('CAMPAIGN.OPERATIONS.TRACKED_LINKS.OPENED_AT') }}</th>
                <th>{{ t('CAMPAIGN.OPERATIONS.TRACKED_LINKS.COUNT') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak text-n-slate-12">
              <tr
                v-for="trackedLink in latestTrackedLinkClicks"
                :key="trackedLink.id"
                class="[&>td]:py-2"
              >
                <td>
                  {{
                    displayValue(
                      trackedLink.contact_name || trackedLink.contact_id
                    )
                  }}
                </td>
                <td
                  class="max-w-[280px] truncate"
                  :title="trackedLink.original_url"
                >
                  {{ displayValue(trackedLink.original_url) }}
                </td>
                <td class="whitespace-nowrap">
                  {{ formatDate(trackedLink.last_clicked_at) }}
                </td>
                <td class="tabular-nums">
                  {{ displayCount(trackedLink.click_count) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        v-if="latestUnknownDeliveries.length"
        class="border-t border-n-weak pt-4"
      >
        <h4 class="text-xs font-semibold uppercase text-n-slate-11">
          {{ t('CAMPAIGN.OPERATIONS.UNKNOWN.TITLE') }}
        </h4>
        <div class="mt-3 overflow-x-auto">
          <div
            v-if="deliveryReconciliationUIFlags.hasError"
            class="mb-3 rounded-md bg-n-ruby-3 px-3 py-2 text-xs text-n-ruby-11"
          >
            {{ t('CAMPAIGN.OPERATIONS.RECONCILIATION.ACTION_ERROR') }}
          </div>

          <div
            v-if="hasProviderBlockedRetry"
            class="mb-3 rounded-md bg-n-ruby-3 px-3 py-2 text-xs text-n-ruby-11"
            data-test="reconciliation-retry-provider-blocked"
          >
            {{ t('CAMPAIGN.OPERATIONS.RECONCILIATION.RETRY_BLOCKED_PROVIDER') }}
          </div>

          <div v-if="canReconcileDeliveries" class="mb-3">
            <label
              class="mb-1 block text-xs font-medium text-n-slate-11"
              :for="`campaign-reconciliation-reason-${campaignId}`"
            >
              {{ t('CAMPAIGN.OPERATIONS.RECONCILIATION.REASON') }}
            </label>
            <input
              :id="`campaign-reconciliation-reason-${campaignId}`"
              v-model="reconciliationReason"
              class="h-9 w-full rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-10 focus:border-n-brand"
              type="text"
              :placeholder="
                t('CAMPAIGN.OPERATIONS.RECONCILIATION.REASON_PLACEHOLDER')
              "
              data-test="reconciliation-reason"
            />
          </div>

          <table class="w-full min-w-[860px] text-left text-xs">
            <thead class="text-n-slate-11">
              <tr class="[&>th]:pb-2 [&>th]:font-medium">
                <th>{{ t('CAMPAIGN.OPERATIONS.UNKNOWN.ID') }}</th>
                <th>{{ t('CAMPAIGN.OPERATIONS.UNKNOWN.CONTACT') }}</th>
                <th>{{ t('CAMPAIGN.OPERATIONS.UNKNOWN.PROVIDER') }}</th>
                <th>{{ t('CAMPAIGN.OPERATIONS.UNKNOWN.STATE') }}</th>
                <th>{{ t('CAMPAIGN.OPERATIONS.UNKNOWN.REASON') }}</th>
                <th>{{ t('CAMPAIGN.OPERATIONS.UNKNOWN.UPDATED') }}</th>
                <th v-if="hasReconciliationActions">
                  {{ t('CAMPAIGN.OPERATIONS.RECONCILIATION.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak align-top text-n-slate-12">
              <tr
                v-for="delivery in latestUnknownDeliveries"
                :key="delivery.id"
                class="[&>td]:py-2"
              >
                <td class="tabular-nums">{{ delivery.id }}</td>
                <td class="tabular-nums">
                  {{ displayValue(delivery.contact_id) }}
                </td>
                <td class="max-w-[140px] truncate" :title="delivery.provider">
                  {{ displayValue(delivery.provider) }}
                </td>
                <td>
                  <span
                    class="inline-flex max-w-[140px] items-center rounded-md px-2 py-1 font-medium"
                    :class="toneForStatus(delivery.observed_state)"
                  >
                    <span class="truncate">
                      {{ displayValue(delivery.observed_state) }}
                    </span>
                  </span>
                </td>
                <td
                  class="max-w-[220px] truncate"
                  :title="delivery.failure_reason"
                >
                  {{ displayValue(delivery.failure_reason) }}
                </td>
                <td class="whitespace-nowrap">
                  {{ formatDate(delivery.updated_at) }}
                </td>
                <td v-if="hasReconciliationActions">
                  <div class="flex flex-wrap gap-2">
                    <Button
                      v-if="canReconcileDelivery(delivery)"
                      variant="faded"
                      color="teal"
                      size="xs"
                      icon="i-lucide-check"
                      :label="
                        t('CAMPAIGN.OPERATIONS.RECONCILIATION.MARK_DELIVERED')
                      "
                      :is-loading="
                        isReconciliationActionLoading(delivery.id, 'DELIVERED')
                      "
                      :data-test="`reconcile-delivered-${delivery.id}`"
                      @click="reconcileDelivery(delivery, 'DELIVERED')"
                    />
                    <Button
                      v-if="canRetryDeliveryAfterReconciliation(delivery)"
                      variant="faded"
                      color="amber"
                      size="xs"
                      icon="i-lucide-refresh-cw"
                      :label="
                        t(
                          'CAMPAIGN.OPERATIONS.RECONCILIATION.MARK_FAILED_RETRYABLE'
                        )
                      "
                      :is-loading="
                        isReconciliationActionLoading(delivery.id, 'FAILED')
                      "
                      :data-test="`reconcile-failed-${delivery.id}`"
                      @click="reconcileDelivery(delivery, 'FAILED')"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </section>
</template>
